import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { ProgressBar } from "primereact/progressbar";
import { Toast } from "primereact/toast";
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUploadMarketPrices } from "store/queries/marketPrices";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";

const PageContainer = styled.div`
  padding: ${sizer(4)};
  max-width: 800px;
  margin: 0 auto;
`;

const FormField = styled.div`
  margin-bottom: ${sizer(3)};
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: ${sizer(1)};
  color: #495057;
`;

const RequiredStar = styled.span`
  color: #e74c3c;
  margin-left: ${sizer(0.5)};
`;

const HelperText = styled.small`
  color: #6c757d;
  display: block;
  margin-top: ${sizer(1)};
`;

const FileInfo = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: ${sizer(2)};
  margin-bottom: ${sizer(3)};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${sizer(2)};
  justify-content: flex-end;
  margin-top: ${sizer(4)};
`;

const TemplateInfo = styled.div`
  margin-bottom: ${sizer(3)};
`;

export function MarketPricesUpload() {
  const navigate = useNavigate();
  const fileUploadRef = useRef<FileUpload>(null);
  const toast = useRef<Toast>(null);
  const [uploadMarketPrices] = useUploadMarketPrices();

  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);

  const { data: providers } = useGetAllProvidersQuery();

  // Get provider options for multiselect
  const providerOptions =
    providers?.data?.map((provider: any) => ({
      label: provider.name,
      value: provider.id,
    })) || [];

  const handleFileSelect = (event: any) => {
    const file = event.files[0];
    if (file && file.name.endsWith(".csv")) {
      setSelectedFile(file);
      fileUploadRef.current?.clear();
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a valid CSV file",
      });
      fileUploadRef.current?.clear();
    }
  };

  const handleSubmit = async () => {
    if (!displayName.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Display name is required",
      });
      return;
    }

    if (!selectedFile) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a CSV file",
      });
      return;
    }

    if (!selectedProviderIds.length) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select at least one provider",
      });
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadMarketPrices({
        file: selectedFile,
        displayName: displayName.trim(),
        description: description.trim() || undefined,
        providerIds: selectedProviderIds,
      }).unwrap();

      const skipped = result.skippedCount || 0;
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail:
          skipped > 0
            ? `Market prices uploaded successfully. ${skipped} ${
                skipped === 1 ? "entry was" : "entries were"
              } skipped because ${
                skipped === 1 ? "it already exists" : "they already exist"
              }.`
            : "Market prices uploaded successfully",
      });
      navigate("/payer-rules?tab=7"); // Navigate to Market Prices tab
    } catch (error: any) {
      console.error("Upload error:", error);
      // Extract error message from various possible error structures
      let errorMessage = "Failed to upload market prices";

      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.error?.data?.message) {
        errorMessage = error.error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // For longer error messages, show them in a more readable format
      if (errorMessage.length > 100) {
        toast.current?.show({
          severity: "error",
          summary: "Upload Failed",
          detail: errorMessage,
          life: 10000, // Show for 10 seconds for longer messages
          sticky: errorMessage.length > 200, // Make it sticky for very long messages
        });
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    navigate("/payer-rules?tab=7");
  };

  return (
    <PageContainer>
      <Toast ref={toast} />
      <Card title="Upload Market Prices">
        <FormField className="mb-4">
          <Label>
            Display Name <RequiredStar>*</RequiredStar>
          </Label>
          <InputText
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Q4 2024 Market Rates"
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField className="mb-4">
          <Label>Description</Label>
          <InputTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description of this market price dataset"
            rows={3}
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField className="mb-4">
          <Label>
            Provider(s) <RequiredStar>*</RequiredStar>
          </Label>
          <MultiSelect
            value={selectedProviderIds}
            options={providerOptions}
            onChange={(e) => setSelectedProviderIds(e.value)}
            placeholder="Select Provider(s)"
            className="w-full"
            filter
            display="chip"
            showClear
            disabled={isUploading}
          />
          <HelperText>
            Select the providers this market price data applies to. The same
            rates will be applied to all selected providers.
          </HelperText>
        </FormField>

        <FormField>
          <Label>
            CSV File <RequiredStar>*</RequiredStar>
          </Label>
          {selectedFile ? (
            <FileInfo>
              <div>
                <strong>Selected file:</strong> {selectedFile.name}
              </div>
              <div>
                <strong>Size:</strong> {(selectedFile.size / 1024).toFixed(2)}{" "}
                KB
              </div>
              <Button
                label="Remove"
                icon="pi pi-times"
                className="p-button-text p-button-danger p-button-sm mt-2"
                onClick={() => setSelectedFile(null)}
                disabled={isUploading}
              />
            </FileInfo>
          ) : (
            <FileUpload
              ref={fileUploadRef}
              mode="basic"
              name="file"
              accept=".csv"
              maxFileSize={10000000} // 10MB
              chooseLabel="Choose CSV File"
              className="w-full"
              onSelect={handleFileSelect}
              disabled={isUploading}
            />
          )}
          <HelperText className="mt-2">
            Maximum file size: 10MB. The CSV must include columns for
            entity_name, Payer Name, CPT Code, and rate.
          </HelperText>
        </FormField>

        {isUploading && (
          <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
        )}

        <ButtonGroup>
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text"
            onClick={handleCancel}
            disabled={isUploading}
          />
          <Button
            label={isUploading ? "Uploading..." : "Upload"}
            icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
            onClick={handleSubmit}
            disabled={isUploading || !selectedFile || !displayName.trim()}
          />
        </ButtonGroup>
      </Card>
    </PageContainer>
  );
}

export default MarketPricesUpload;
